// Client-side API for shared shopping lists.
// Mirrors src/lib/sharedCalendars.js, but for the shopping_recipes/items pair.
//
// Schema (see supabase/schema.sql section 10):
//   shared_shopping_lists          (id, name, owner_id, created_at)
//   shared_shopping_list_members   (list_id, user_id, joined_at)
//   shared_shopping_list_invites   (token, list_id, created_by, created_at)
//   shopping_recipes.shared_list_id  -- NULL for personal groups
//   shopping_items.shared_list_id    -- NULL for personal items
//
// All functions assume the caller is authenticated; RLS enforces access.

import { supabase } from './supabase';

const buildInviteUrl = (token) => {
  const { origin, pathname } = window.location;
  const base = `${origin}${pathname.replace(/\/+$/, '')}`;
  return `${base}?joinShoppingList=${encodeURIComponent(token)}`;
};

const randomToken = () => {
  const arr = new Uint8Array(24);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(36).padStart(2, '0'))
    .join('')
    .slice(0, 28);
};

// Returns an array of { id, name, ownerId, isOwner, createdAt }.
export async function listSharedShoppingLists() {
  const {
    data: { user }
  } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('shared_shopping_lists')
    .select('id, name, owner_id, created_at')
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data || []).map((r) => ({
    id: r.id,
    name: r.name,
    ownerId: r.owner_id,
    isOwner: r.owner_id === user.id,
    createdAt: r.created_at
  }));
}

// Create a new shared shopping list owned by the current user.
// Returns { id, name, inviteToken }.
export async function createSharedShoppingList(name) {
  const trimmed = (name || '').trim();
  if (!trimmed) throw new Error('Name is required');

  const { data, error } = await supabase.rpc('create_shared_shopping_list', {
    list_name: trimmed
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('No row returned from create_shared_shopping_list');
  return {
    id: row.id,
    name: row.name,
    inviteToken: row.invite_token
  };
}

// Rename a shared list (owner only — RLS enforced).
export async function renameSharedShoppingList(listId, newName) {
  const trimmed = (newName || '').trim();
  if (!trimmed) throw new Error('Name is required');
  const { error } = await supabase
    .from('shared_shopping_lists')
    .update({ name: trimmed })
    .eq('id', listId);
  if (error) throw error;
}

// Fetch (or create on demand) the reusable invite URL for a list the caller owns.
export async function getShoppingListInviteUrl(listId) {
  const { data, error } = await supabase
    .from('shared_shopping_list_invites')
    .select('token')
    .eq('list_id', listId)
    .maybeSingle();
  if (error && error.code !== 'PGRST116') throw error;
  if (!data) {
    const {
      data: { user }
    } = await supabase.auth.getUser();
    const token = randomToken();
    const { error: insErr } = await supabase
      .from('shared_shopping_list_invites')
      .insert({ token, list_id: listId, created_by: user.id });
    if (insErr) throw insErr;
    return buildInviteUrl(token);
  }
  return buildInviteUrl(data.token);
}

// Join a shared shopping list via an invite token. Returns the list id.
export async function joinSharedShoppingListByToken(token) {
  const { data, error } = await supabase.rpc('join_shared_shopping_list', {
    invite_token: token
  });
  if (error) throw error;
  return data; // uuid
}

// Delete a shared list (owner only). Cascades to members/invites/groups/items.
export async function deleteSharedShoppingList(listId) {
  const { error } = await supabase
    .from('shared_shopping_lists')
    .delete()
    .eq('id', listId);
  if (error) throw error;
}

// List members of a shared shopping list the caller belongs to.
export async function listSharedShoppingListMembers(listId) {
  const { data, error } = await supabase.rpc(
    'list_shared_shopping_list_members',
    { lid: listId }
  );
  if (error) throw error;
  return (data || []).map((r) => ({
    userId: r.user_id,
    email: r.email,
    displayName: r.display_name,
    avatarEmoji: r.avatar_emoji,
    avatarColor: r.avatar_color,
    avatarUrl: r.avatar_url,
    avatarKind: r.avatar_kind || 'emoji',
    isOwner: r.is_owner,
    joinedAt: r.joined_at
  }));
}

// Remove the current user from a shared shopping list.
export async function leaveSharedShoppingList(listId) {
  const {
    data: { user }
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Not signed in');
  const { error } = await supabase
    .from('shared_shopping_list_members')
    .delete()
    .eq('list_id', listId)
    .eq('user_id', user.id);
  if (error) throw error;
}
